import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import {
  useAdjustAutopilotNotificationCredits,
  useAutopilotNotificationProviders,
  useAutopilotNotificationTransactions,
} from '@/features/autopilot/hooks/useAutopilotCredits';

const channelLabels: Record<'sms' | 'email', { titleKey: string; subtitleKey: string }> = {
  sms: {
    titleKey: 'prototype.credits.channels.sms.title',
    subtitleKey: 'prototype.credits.channels.sms.subtitle',
  },
  email: {
    titleKey: 'prototype.credits.channels.email.title',
    subtitleKey: 'prototype.credits.channels.email.subtitle',
  },
};

const AutopilotNotificationCreditsCard = () => {
  const { t } = useTranslation('autopilot');
  const { toast } = useToast();
  const providersQuery = useAutopilotNotificationProviders();
  const [dialogChannel, setDialogChannel] = useState<'sms' | 'email' | null>(null);
  const [amountCents, setAmountCents] = useState('');
  const [note, setNote] = useState('');
  const [adjustType, setAdjustType] = useState<'credit' | 'adjustment'>('credit');
  const adjustMutation = useAdjustAutopilotNotificationCredits();
  const transactionsQuery = useAutopilotNotificationTransactions(dialogChannel, 20);

  const closeDialog = () => {
    setDialogChannel(null);
    setAmountCents('');
    setNote('');
    setAdjustType('credit');
  };

  const providers = useMemo(() => providersQuery.data ?? [], [providersQuery.data]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!dialogChannel) {
      return;
    }
    const parsedAmount = Number.parseInt(amountCents.trim(), 10);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      toast({
        title: t('prototype.credits.toast.invalidAmountTitle', { defaultValue: 'Invalid amount' }),
        description: t('prototype.credits.toast.invalidAmountDescription', {
          defaultValue: 'Enter a positive amount in cents.',
        }),
        variant: 'destructive',
      });
      return;
    }
    try {
      await adjustMutation.mutateAsync({
        channel: dialogChannel,
        amountCents: parsedAmount,
        type: adjustType,
        note: note.trim() ? note.trim() : undefined,
      });
      toast({
        title: t('prototype.credits.toast.successTitle', { defaultValue: 'Credits updated' }),
        description: t('prototype.credits.toast.successDescription', { defaultValue: 'Notification credits adjusted.' }),
      });
      closeDialog();
    } catch (error) {
      toast({
        title: t('prototype.credits.toast.errorTitle', { defaultValue: 'Unable to update credits' }),
        description:
          error instanceof Error
            ? error.message
            : t('prototype.credits.toast.errorDescription', { defaultValue: 'Please try again shortly.' }),
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Card className="border-slate-200/70 bg-white/80 shadow-sm dark:border-slate-800/60 dark:bg-slate-900/60">
        <CardHeader>
          <CardTitle>{t('prototype.credits.title', { defaultValue: 'Notification credits' })}</CardTitle>
          <CardDescription>
            {t('prototype.credits.subtitle', {
              defaultValue: 'Track SMS and email balances, then top up credits as needed.',
            })}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {providersQuery.isLoading ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            providers.map((provider) => {
              const labels = channelLabels[provider.channel];
              return (
                <div
                  key={provider.channel}
                  className="flex flex-col gap-3 rounded-xl border border-slate-200/60 bg-white/80 p-4 dark:border-slate-800/50 dark:bg-slate-900/50 lg:flex-row lg:items-center lg:justify-between"
                >
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">
                        {t(labels.titleKey, {
                          defaultValue: provider.channel === 'sms' ? 'SMS campaigns' : 'Email campaigns',
                        })}
                      </span>
                      <Badge variant="outline" className="capitalize">
                        {provider.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t(labels.subtitleKey, {
                        defaultValue:
                          provider.channel === 'sms'
                            ? 'Twilio credits fund outbound SMS automations.'
                            : 'Postmark credits fund outbound email automations.',
                      })}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-6">
                    <div className="flex flex-col">
                      <span className="text-xs uppercase text-muted-foreground">
                        {t('prototype.credits.balance', { defaultValue: 'Balance' })}
                      </span>
                      <span className="text-sm font-semibold">
                        {t('prototype.credits.balanceValue', {
                          defaultValue: '{{cents}}¢',
                          cents: provider.creditBalanceCents,
                        })}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs uppercase text-muted-foreground">
                        {t('prototype.credits.unitCost', { defaultValue: 'Unit cost' })}
                      </span>
                      <span className="text-sm font-medium">
                        {t('prototype.credits.unitCostValue', {
                          defaultValue: '{{cents}}¢',
                          cents: provider.unitCostCents,
                        })}
                      </span>
                    </div>
                    <Button size="sm" onClick={() => setDialogChannel(provider.channel)}>
                      {t('prototype.credits.addCredits', { defaultValue: 'Add credits' })}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogChannel !== null} onOpenChange={(open) => (!open ? closeDialog() : undefined)}>
        <DialogContent className="max-w-xl">
          <form className="space-y-5" onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>
                {t('prototype.credits.dialog.title', {
                  defaultValue: 'Add credits',
                })}
              </DialogTitle>
              <DialogDescription>
                {dialogChannel
                  ? t('prototype.credits.dialog.subtitle', {
                      defaultValue: 'Adjust credits for {{channel}} notifications.',
                      channel:
                        dialogChannel === 'sms'
                          ? t('prototype.credits.channels.sms.title', { defaultValue: 'SMS campaigns' })
                          : t('prototype.credits.channels.email.title', { defaultValue: 'Email campaigns' }),
                    })
                  : null}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="credit-amount">
                  {t('prototype.credits.dialog.amountLabel', { defaultValue: 'Amount (cents)' })}
                </Label>
                <Input
                  id="credit-amount"
                  type="number"
                  min={1}
                  value={amountCents}
                  onChange={(event) => setAmountCents(event.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="credit-type">
                  {t('prototype.credits.dialog.typeLabel', { defaultValue: 'Adjustment type' })}
                </Label>
                <Select value={adjustType} onValueChange={(value) => setAdjustType(value as 'credit' | 'adjustment')}>
                  <SelectTrigger id="credit-type">
                    <SelectValue placeholder={t('prototype.credits.dialog.typePlaceholder', { defaultValue: 'Select type' })} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">
                      {t('prototype.credits.dialog.typeCredit', { defaultValue: 'Credit (increase)' })}
                    </SelectItem>
                    <SelectItem value="adjustment">
                      {t('prototype.credits.dialog.typeAdjustment', { defaultValue: 'Adjustment (manual)' })}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="credit-note">
                  {t('prototype.credits.dialog.noteLabel', { defaultValue: 'Note (optional)' })}
                </Label>
                <Input
                  id="credit-note"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder={t('prototype.credits.dialog.notePlaceholder', {
                    defaultValue: 'Reason for credit adjustment',
                  }) ?? ''}
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">
                {t('prototype.credits.transactions.title', { defaultValue: 'Recent transactions' })}
              </h4>
              {transactionsQuery.isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-5 w-2/3" />
                </div>
              ) : transactionsQuery.data && transactionsQuery.data.length > 0 ? (
                <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200/60 dark:border-slate-800/60">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('prototype.credits.transactions.columns.type', { defaultValue: 'Type' })}</TableHead>
                        <TableHead>{t('prototype.credits.transactions.columns.amount', { defaultValue: 'Amount' })}</TableHead>
                        <TableHead>{t('prototype.credits.transactions.columns.description', { defaultValue: 'Description' })}</TableHead>
                        <TableHead>{t('prototype.credits.transactions.columns.timestamp', { defaultValue: 'Timestamp' })}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactionsQuery.data.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell className="capitalize">{transaction.type}</TableCell>
                          <TableCell>{t('prototype.credits.balanceValue', { defaultValue: '{{cents}}¢', cents: transaction.amountCents })}</TableCell>
                          <TableCell>{transaction.description ?? '-'}</TableCell>
                          <TableCell>{new Date(transaction.createdAt).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('prototype.credits.transactions.empty', { defaultValue: 'No transactions recorded yet.' })}
                </p>
              )}
            </div>

            <DialogFooter className="flex flex-row justify-end gap-3">
              <Button type="button" variant="outline" onClick={closeDialog}>
                {t('prototype.credits.dialog.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button type="submit" disabled={adjustMutation.isPending}>
                {adjustMutation.isPending
                  ? t('prototype.credits.dialog.saving', { defaultValue: 'Saving…' })
                  : t('prototype.credits.dialog.submit', { defaultValue: 'Apply credits' })}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AutopilotNotificationCreditsCard;
